import React from 'react';
import { Facebook, Instagram, Mail, MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import webResonanzIcon from '@/assets/web-resonanz-icon.svg.asset.json';

const Footer = () => {
  const { t } = useTranslation();
  
  return (
    <footer className="bg-slate-900 border-t border-amber-500/20">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Kontaktinfo */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold text-amber-400 mb-4">{t('footer.contact')}</h3>
            <div className="space-y-3 text-slate-300">
              <div className="flex items-center space-x-3">
                <MapPin size={18} className="text-amber-400" />
                <span style={{ whiteSpace: 'pre-line' }}>{t('footer.address')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone size={18} className="text-amber-400" />
                <span>{t('footer.phone')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail size={18} className="text-amber-400" />
                <span>{t('footer.email')}</span>
              </div>
            </div>
          </div>

          {/* Trainingszeiten */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold text-amber-400 mb-4">{t('footer.trainingTimes')}</h3>
            <div className="space-y-2 text-slate-300">
              <p>{t('footer.monday')}</p>
              <p>{t('footer.tuesday')}</p>
              <p>{t('footer.wednesday')}</p>
              <p>{t('footer.thursday')}</p>
              <p>{t('footer.friday')}</p>
            </div>
          </div>

          {/* Schnelllinks */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold text-amber-400 mb-4">{t('footer.quickLinks')}</h3>
            <div className="space-y-2 text-slate-300">
              <Link to="/downloads" className="block hover:text-amber-400 transition-colors">
                {t('footer.downloads')}
              </Link>
              <Link to="/blog" className="block hover:text-amber-400 transition-colors">
                {t('footer.news')}
              </Link>
              <Link to="/tournaments" className="block hover:text-amber-400 transition-colors">
                {t('footer.tournaments')}
              </Link>
              <Link to="/training" className="block hover:text-amber-400 transition-colors">
                {t('footer.training')}
              </Link>
              <Link to="/gallery" className="block hover:text-amber-400 transition-colors">
                {t('footer.gallery')}
              </Link>
              <a 
                href="https://archiv.asc-fechten.com/" 
                target="_blank"
                rel="noopener noreferrer"
                className="block hover:text-amber-400 transition-colors"
              >
                {t('footer.archive')}
              </a>
            </div>
          </div>

          {/* Social Media */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold text-amber-400 mb-4">{t('footer.followUs')}</h3>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/people/ASC-Fechten/100057153112929/" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-amber-400 transition-colors"
                aria-label={t('header.facebookLabel')}
              >
                <Facebook size={24} />
              </a>
              <a 
                href="https://www.instagram.com/asc_fechten/" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-amber-400 transition-colors"
                aria-label={t('header.instagramLabel')}
              >
                <Instagram size={24} />
              </a>
            </div>
          </div>

          {/* Bankverbindung */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-semibold text-amber-400 mb-4">{t('footer.bankDetails')}</h3>
            <div className="space-y-2 text-slate-300 text-sm">
              <div>
                <span className="font-medium text-amber-400">IBAN:</span>
                <br />
                <span>AT52 3200 0000 0254 7628</span>
              </div>
              <div>
                <span className="font-medium text-amber-400">BIC:</span>
                <br />
                <span>RLNWATWW</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-amber-500/20 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-slate-400">
              {t('footer.copyright')}
            </p>
            
            <a 
              href="https://web-resonanz.at/" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-slate-400 hover:text-amber-400 transition-colors"
            >
              <img 
                src={webResonanzIcon.url}
                alt="Web-Resonanz Logo"
                className="h-6 w-auto object-contain"
                width="24"
                height="24"
                loading="lazy"
              />
              <span className="text-sm">{t('footer.webdesign')}</span>
            </a>
            
            <div className="flex space-x-6 text-sm">
              <Link to="/impressum" className="text-slate-400 hover:text-amber-400 transition-colors">
                {t('footer.impressum')}
              </Link>
              <Link to="/datenschutz" className="text-slate-400 hover:text-amber-400 transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to="/agb" className="text-slate-400 hover:text-amber-400 transition-colors">
                {t('footer.terms')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
